import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Loader2, AlertCircle } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import { useSiteContent } from '../../hooks/useSiteContent';

const ICON_MAP = {
  phone: Phone,
  email: Mail,
  location: MapPin,
  hours: Clock,
};

const SiteContactPage = () => {
  const { page, loading, error } = useSiteContent('contact');
  const data = page?.content_json || {};
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="max-w-6xl mx-auto px-4 py-12 pt-24">
        {loading && (
          <div className="flex justify-center py-20">
            <Loader2 className="h-10 w-10 animate-spin text-purple-600" />
          </div>
        )}

        {!loading && error && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700 flex gap-3">
            <AlertCircle className="h-5 w-5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {!loading && page && (
          <>
            <h1 className="text-4xl font-bold mb-4">{page.title || data.title || 'Contact Us'}</h1>
            <p className="text-gray-600 mb-12">{page.subtitle || data.intro || ''}</p>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-6">
                {(data.cards || []).map((card, index) => {
                  const Icon = ICON_MAP[card.type] || Mail;
                  return (
                    <div key={index} className="bg-white rounded-2xl shadow-lg p-6">
                      <div className="flex items-start space-x-4">
                        <div className="bg-purple-100 p-3 rounded-lg">
                          <Icon size={24} className="text-purple-600" />
                        </div>
                        <div>
                          <h3 className="font-bold mb-1">{card.title}</h3>
                          <p className="text-gray-700">{card.value}</p>
                          {card.note && <p className="text-sm text-gray-500">{card.note}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6">{data.form?.title || 'Send us a message'}</h2>
                {submitted && (
                  <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4">
                    {data.form?.success_message || "Thank you! We'll get back to you soon."}
                  </div>
                )}
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Message</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition"
                  >
                    {data.form?.submit_label || 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default SiteContactPage;
